import { inject, Injectable, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PushNotifications } from '@capacitor/push-notifications';
import { BaseComponent } from '../../shared/component-base';
import { NotificationTypeWithoutSuffix, Permission } from './models/user';
import { RestApiService } from './rest-api-service';
import { getServiceTypeUrl } from './utility';
import { ServiceByApi } from './models/service';

type PushNotificationActionData = {
  'google.delivered_priority': string;
  'google.original_priority': string;
  program_id?: string;
  service_id?: string;
  from: string;
  push: NotificationTypeWithoutSuffix;
  owner_id?: string;
  collapse_key: string;
  // provider_id, service_id, user_id, program_id, etc. may be present depending on the notification type
};
@Injectable({ providedIn: 'root' })
export class PushService extends BaseComponent implements OnInit {
  readonly router = inject(Router);
  private zone: NgZone = inject(NgZone);

  public override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  async addListeners(): Promise<void> {
    await PushNotifications.addListener('registration', token => {
      console.info('PushService - Registration token: ', token.value);
      this.restApiService.savePushToken(token.value).subscribe(() => {
        console.log('PushService - Push token registered: ', token.value);
      });
    });

    await PushNotifications.addListener('registrationError', err => {
      console.error('PushService - Registration error: ', err.error);
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      notification => {
        console.log('PushService - Push notification received: ', notification);
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      notification => {
        const data = this.getNotificationData(notification);

        const navLinkArrayOrPromise = this.createNavigationLink(data);

        this.zone.run(() => {
          console.log(
            'PushService - Push notification action performed',
            navLinkArrayOrPromise,
            notification,
            this.router
          );
          if (Array.isArray(navLinkArrayOrPromise)){
            this.router.navigate(navLinkArrayOrPromise);
          }
          else{
            navLinkArrayOrPromise.then((navLink)=>{
              this.router.navigate(navLink);
            })
          }
        });
      }
    );
  }

  public getNotificationData(notification: any): PushNotificationActionData {
    if (notification?.notification?.data?.aps?.push) {
      // ios has its push data in notification.notification.data.aps
      return notification.notification.data.aps as PushNotificationActionData;
    } else {
      // every other normal platform has it in notification.notification.data
      return notification.notification.data as PushNotificationActionData;
    }
  }

  public createNavigationLink(data: PushNotificationActionData): string[] | Promise<string[]> {
    const lang = this.translateService.currentLang;
    const type = this.me?.type;
    switch (data.push) {
      case NotificationTypeWithoutSuffix.PROGRAM_DELETED: {
        if (type === Permission.OWNER) {
          return [lang, 'profile-programs'];
        } else if (type === Permission.PROVIDER) {
          return [lang, 'provider-programs'];
        } else {
          return [lang, 'home'];
        }
      }
      case NotificationTypeWithoutSuffix.PROGRAM_UPDATED: {
        const programId = data['program_id'];
        return [lang, 'programs', programId ?? ''];
      }
      case NotificationTypeWithoutSuffix.PROGRAM_RECOMMEND: {
        const programId = data['program_id'];
        return [lang, 'programs', programId ?? ''];
      }
      case NotificationTypeWithoutSuffix.PROGRAM_INTERESTED: {
        const programId = data['program_id'];
        return [lang, 'programs', programId ?? ''];
      }
      case NotificationTypeWithoutSuffix.PROGRAM_JOINED: {
        const programId = data['program_id'];
        return [lang, 'programs', programId ?? ''];
      }
      case NotificationTypeWithoutSuffix.AD_MESSAGE: {
        return [lang, 'profile-ads'];
      }
      case NotificationTypeWithoutSuffix.BLOG_NEW: {
        return [lang, 'blog'];
      }
      case NotificationTypeWithoutSuffix.KENNEL_JOINED: {
        return [lang, 'profile-dogs'];
      }
      case NotificationTypeWithoutSuffix.KENNEL_REQUEST: {
        return [lang, 'profile-dogs'];
      }
      case NotificationTypeWithoutSuffix.PROGRAM_REVIEWED:
      case NotificationTypeWithoutSuffix.PROGRAM_REVIEW_REPLIED: {
        return [lang, 'programs', data['program_id'] ?? ''];
      }
      case NotificationTypeWithoutSuffix.SERVICE_REVIEWED:
      case NotificationTypeWithoutSuffix.SERVICE_REVIEW_REPLIED: {
        var providerId: any = data['service_id'];
        return new Promise((resolve) => {
          this.restApiService.getService(providerId).subscribe({
            next: value => {
              const provider = value as ServiceByApi;
              const parsedServiceType = getServiceTypeUrl(provider.type);
              
              resolve([lang, 'provider', parsedServiceType, provider.id+'']);
            },
            error: err => {
              console.log('error: ', err);
              return [lang, 'home'];
            },
          });
        })

      }
      default:
        return [lang, 'home'];
    }
  }

  async registerNotifications(): Promise<boolean> {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.log('PushService - User denied permissions!');
      return false;
    }

    await PushNotifications.register();
    return true;
  }

  async unregisterNotifications(): Promise<void> {
    await PushNotifications.unregister();

    PushNotifications.removeAllListeners();

    this.restApiService.deletePushToken().subscribe(() => {
      console.log('PushService - Unregistered from push notifications');
    });
  }

  async getDeliveredNotifications(): Promise<void> {
    const notificationList =
      await PushNotifications.getDeliveredNotifications();
    console.log('PushService - delivered notifications', notificationList);
  }
}
